'use client';
import Link from 'next/link';
import { EchoNavbar } from '@/features/marketing/components/echo/EchoNavbar';
import { EchoFooter } from '@/features/marketing/components/echo/EchoFooter';
import type { PseoPage } from '@/features/marketing/lib/pseo/pseo-core';
import { buildPseoJsonLd } from '@/features/marketing/lib/pseo/pseo-jsonld';
import type { FaqItem } from '@/data/pseo/content';
import { industryContent, useCaseContent, competitorContent } from '@/data/pseo/content';
import { getMinimoesPseoContent, type Section } from '@/features/marketing/lib/pseo/pseo-content';
import { getPseoImageUrl } from '@/features/marketing/lib/pseo/pseo-images';
import { PseoFaq } from '@/features/marketing/components/pseo/PseoFaq';
import { EchoGetStartedButton } from '@/features/marketing/components/echo/EchoGetStartedButton';
import { LAYOUT, TYPE } from '@/features/marketing/components/_styles';
import { NAV_ACTIONS } from '@/features/marketing/data/navigation.data';
import { SITE_NAME } from '@/shared/lib/config';

const PSEO_CTA = {
  headline: 'Give your AI a face. Free.',
  subline: 'No credit card · Live in minutes · Trained on your content',
  button: 'Create yours',
};

// Article headings on the landing type scale (same family and weight as the landing titles).
const H2 = 'text-xl sm:text-[22px] font-medium tracking-[-0.02em] leading-snug text-[#181a1a]';
const H3 = 'text-[17px] font-medium tracking-[-0.01em] text-[#181a1a]';

// ─── JSON-LD helper ───────────────────────────────────────────────────────────

function safeJsonLd(data: unknown): string {
  return JSON.stringify(data)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/\//g, '\\u002f');
}

// ─── Hub routing map ──────────────────────────────────────────────────────────

const HUB_PATHS = {
  industry:   { href: '/ai-avatar',          label: 'AI Mascot by Industry' },
  'use-case': { href: '/ai-avatar/use-case', label: 'Use Cases' },
  comparison: { href: '/compare',            label: 'Compare' },
} as const;

// ─── Page layout ──────────────────────────────────────────────────────────────

interface Props { page: PseoPage }

export function PseoPageLayout({ page }: Props) {
  const isComparison = page.kind === 'comparison';
  const isIndustry   = page.kind === 'industry';
  const isUseCase    = page.kind === 'use-case';

  const industryKey   = page.data.industry        ?? '';
  const useCaseKey    = page.data.use_case         ?? '';
  const competitorKey = page.data.competitor       ?? '';
  const competitorLabel = page.data.competitor_label ?? competitorKey;

  const richContent = isIndustry ? (industryContent[industryKey] ?? null)
    : isUseCase ? (useCaseContent[useCaseKey] ?? null)
    : null;

  const compContent = isComparison ? (competitorContent[competitorKey] ?? null) : null;

  const headline = richContent?.headline ?? compContent?.headline ?? page.keyword;
  const subheadline = richContent?.subheadline ?? compContent?.subheadline ?? page.description;

  const faqs: FaqItem[] = richContent?.faqs ?? compContent?.faqs ?? [];
  const jsonLd = buildPseoJsonLd(page, faqs);

  const hub = (HUB_PATHS as Record<string, { href: string; label: string } | undefined>)[page.kind];
  const slugKey = page.slug.split('/').filter(Boolean).pop() ?? '';
  const shareUrl = `https://minimoes.com/${page.slug}`;
  const kindTag = hub?.label ?? 'AI Mascot';

  // Generate rich editorial sections from the page label
  const pageLabel = page.data.industry ?? page.data.use_case ?? competitorLabel ?? page.keyword;
  const generatedContent = getMinimoesPseoContent(
    page.kind as 'industry' | 'use-case' | 'comparison',
    pageLabel,
    competitorLabel
  );
  const sections: Section[] = generatedContent.sections;

  const widgetTitleStyle = {
    fontSize: 18,
    fontWeight: 600,
    color: '#000',
    paddingBottom: 12,
    marginBottom: 20,
    marginTop: 0,
    borderBottom: '3px solid #000',
    display: 'block' as const,
  };

  const sidebarLinks = page.links.filter(l => l.rel === 'sibling');

  return (
    <div className="bg-white min-h-screen font-sans antialiased">
      <EchoNavbar />

      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(jsonLd.breadcrumb) }} />
      {jsonLd.faq && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(jsonLd.faq) }} />}
      {jsonLd.software && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(jsonLd.software) }} />}

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="max-w-[1250px] mx-auto px-6 pt-6">
        <ol className="flex items-center gap-1.5" style={{ fontSize: 12, color: '#646464' }}>
          <li><Link href="/" style={{ color: '#646464' }} className="hover:text-black transition-colors">Home</Link></li>
          <li aria-hidden="true" className="select-none">/</li>
          {hub && (
            <>
              <li><Link href={hub.href} style={{ color: '#646464' }} className="hover:text-black transition-colors">{hub.label}</Link></li>
              <li aria-hidden="true" className="select-none">/</li>
            </>
          )}
          <li style={{ color: '#646464' }} className="truncate max-w-[200px] sm:max-w-none">{page.title}</li>
        </ol>
      </nav>

      {/* Main */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-[1250px] mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-[1fr_300px] gap-x-16 xl:gap-x-24 items-start">

            {/* ── ARTICLE ── */}
            <div>

              {/* A. Entry header */}
              <div className="text-center" style={{ paddingBottom: 60 }}>
                <h1 className={`${TYPE.title} text-[#181a1a] mb-[30px]`}>
                  {headline}
                </h1>
                <ul style={{ maxWidth: 380, margin: '0 auto', fontSize: 13, color: '#646464', listStyle: 'none', padding: 0 }}>
                  <li>
                    <Link href="#" style={{ color: '#646464', textDecoration: 'none', marginRight: 8 }} className="hover:text-black transition-colors">
                      {kindTag}
                    </Link>
                  </li>
                </ul>
                <p style={{ fontSize: 16, fontWeight: 500, maxWidth: 560, margin: '30px auto 0', lineHeight: 1.7, color: '#3d3d3d' }}>
                  {subheadline}
                </p>
              </div>

              {/* B. Hero image */}
              <div style={{ marginBottom: 50, borderRadius: 4, overflow: 'hidden', backgroundColor: '#f5f5f5' }}>
                <img
                  src={getPseoImageUrl(slugKey, 1000, 400)}
                  alt={headline}
                  style={{ width: '100%', height: 360, objectFit: 'cover', display: 'block' }}
                />
              </div>

              {/* C. Entry content */}
              <div className="px-6 lg:px-[60px]" style={{ paddingTop: 50, paddingBottom: 50, fontSize: 16, lineHeight: 1.7, color: '#3d3d3d' }}>

                {/* Industry / use-case sections */}
                {sections.map((section, idx) => (
                  <section key={idx}>
                    <h2 className={`${H2} mb-3 ${idx === 0 ? '' : 'mt-10'}`}>
                      {section.heading}
                    </h2>
                    {idx === 0 && section.body ? (
                      <p className="has-dropcap" style={{ fontSize: 15, lineHeight: 1.8, marginBottom: 20 }}>
                        <span className="float-left text-[52px] font-medium leading-[0.8] mr-2 mt-1.5 text-[#181a1a]">
                          {section.body[0]}
                        </span>
                        {section.body.slice(1)}
                      </p>
                    ) : section.body ? (
                      <p style={{ fontSize: 15, lineHeight: 1.8, marginBottom: 20 }}>{section.body}</p>
                    ) : null}
                    {section.bullets && (
                      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px 0' }}>
                        {section.bullets.map((bullet, bIdx) => (
                          <li key={bIdx} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 10, color: '#3d3d3d' }}>
                            <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#000', marginRight: 12, marginTop: '0.45em', flexShrink: 0 }} />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {section.sub && section.sub.map((sub, sIdx) => (
                      <div key={sIdx} style={{ marginTop: 24 }}>
                        <h3 className={`${H3} mb-2`}>{sub.heading}</h3>
                        <p style={{ fontSize: 15, lineHeight: 1.8, marginBottom: 20, color: '#3d3d3d' }}>{sub.body}</p>
                      </div>
                    ))}
                  </section>
                ))}

                {/* Comparison content */}
                {compContent && isComparison && (
                  <>
                    <p style={{ fontSize: 15, lineHeight: 1.8, marginBottom: 20 }}>{compContent.switchReason}</p>
                    {compContent.comparisonRows.length > 0 && (
                      <div className="overflow-x-auto" style={{ marginTop: 24, marginBottom: 24 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f9f9f9' }}>
                              <th style={{ fontWeight: 600, textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid #e5e5e5', color: '#000' }}>Feature</th>
                              <th style={{ fontWeight: 600, textAlign: 'center', padding: '10px 14px', borderBottom: '1px solid #e5e5e5', color: '#000' }}>{SITE_NAME}</th>
                              <th style={{ fontWeight: 600, textAlign: 'center', padding: '10px 14px', borderBottom: '1px solid #e5e5e5', color: '#000' }}>{competitorLabel}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {compContent.comparisonRows.map((row, rIdx) => (
                              <tr key={rIdx}>
                                <td style={{ padding: '10px 14px', borderBottom: '1px solid #e5e5e5', color: '#3d3d3d' }}>{row.feature}</td>
                                <td style={{ padding: '10px 14px', borderBottom: '1px solid #e5e5e5', textAlign: 'center', fontWeight: row.winner === 'minimoes' ? 700 : 400, color: '#000' }}>{row.minimoes}</td>
                                <td style={{ padding: '10px 14px', borderBottom: '1px solid #e5e5e5', textAlign: 'center', fontWeight: row.winner === 'competitor' ? 700 : 400, color: '#3d3d3d' }}>{row.competitor}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* D. Entry footer */}
              <div className="px-6 lg:px-[60px] text-center" style={{ paddingTop: 40, paddingBottom: 0 }}>
                <p style={{ fontSize: 12, color: '#989898', marginBottom: 16 }}>Updated 2025</p>
                <div>
                  <span style={{ fontSize: 13, color: '#3d3d3d', fontWeight: 600 }}>Tags: </span>
                  {[kindTag, 'AI Mascot', 'Minimoes'].map((tag, i) => (
                    <Link key={i} href="#" style={{ fontSize: 13, color: '#5a5959', marginLeft: 4, textDecoration: 'none' }} className="hover:text-black transition-colors">{tag}</Link>
                  ))}
                </div>
                <div style={{ marginTop: 15, paddingTop: 15 }}>
                  <span style={{ fontSize: 13, color: '#3d3d3d' }}>Share: </span>
                  <a href={`https://www.linkedin.com/shareArticle?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, color: '#3d3d3d', marginLeft: 12, textDecoration: 'none' }} className="hover:text-black transition-colors">LinkedIn</a>
                  <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, color: '#3d3d3d', marginLeft: 12, textDecoration: 'none' }} className="hover:text-black transition-colors">Twitter</a>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, color: '#3d3d3d', marginLeft: 12, textDecoration: 'none' }} className="hover:text-black transition-colors">Facebook</a>
                </div>
                {/* Author */}
                <div style={{ maxWidth: 320, margin: '50px auto 10px', textAlign: 'center' }}>
                  <img src="https://i.pravatar.cc/80?img=23" alt="Minimoes Team" style={{ width: 75, height: 75, borderRadius: '50%', marginBottom: 14, display: 'inline-block' }} />
                  <h5 className={`${H3} mb-2`}>{SITE_NAME} Team</h5>
                  <p style={{ fontSize: 13, color: '#646464', lineHeight: 1.6, margin: 0 }}>We build talking 3D AI mascots that greet, answer, sell and book, 24/7.</p>
                </div>
              </div>

              {/* E. FAQ */}
              {faqs.length > 0 && (
                <div className="px-6 lg:px-[60px]" style={{ paddingTop: 20, paddingBottom: 50 }}>
                  <PseoFaq items={faqs} />
                </div>
              )}

            </div>
            {/* END ARTICLE */}

            {/* ── SIDEBAR ── */}
            <aside className="hidden lg:block sticky top-24">
              {sidebarLinks.length > 0 && (
                <div style={{ marginBottom: 40 }}>
                  <h4 style={widgetTitleStyle}>Explore more</h4>
                  <div>
                    {sidebarLinks.slice(0, 6).map(link => {
                      const ls = link.href.split('/').filter(Boolean).pop() ?? '';
                      return (
                        <div key={link.href} style={{ display: 'flex', gap: 12, marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f1f1f1', alignItems: 'flex-start' }}>
                          <img src={getPseoImageUrl(ls, 80, 80)} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 3, flexShrink: 0 }} />
                          <div>
                            <h6 style={{ margin: 0 }}>
                              <Link href={link.href} className="text-[13px] font-medium leading-snug text-[#181a1a] hover:underline">
                                {link.label}
                              </Link>
                            </h6>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </aside>

          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`bg-white text-center ${LAYOUT.section} ${LAYOUT.gutter}`}>
        <h2 className={`${TYPE.title} text-[#181a1a]`}>{PSEO_CTA.headline}</h2>
        <p className={`mt-4 ${TYPE.body} text-[#7d7d7d]`}>{PSEO_CTA.subline}</p>
        <div className="mt-8 flex justify-center">
          <EchoGetStartedButton href={NAV_ACTIONS.signup.href} label={PSEO_CTA.button} hoverLabel={NAV_ACTIONS.signup.hoverLabel} />
        </div>
      </section>

      <EchoFooter />
    </div>
  );
}
